import React, { useEffect, useRef } from 'react';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useCC } from '../../contexts/CCContext';
import useCCResource from './useCCResource';
import { CCResourceState, CCError } from './CCFeedback';
import { readRegionSiblings } from './CCGeography';

export { readRegionSiblings } from './CCGeography';

function RegionLevel({ parent, level, path, selected }) {
  const cc = useCC(); const { t } = useTranslation('translation');
  const key = cc.active && cc.snapshot && parent && !cc.navigation?.loading && !cc.navigation?.error
    ? JSON.stringify([cc.accessKey, cc.snapshot.boundary_revision_id, parent.id, cc.refresh]) : null;
  const result = useCCResource(key, (signal) => cc.geography
    ? cc.geography.load(parent.id, signal) : readRegionSiblings(cc.snapshot, parent.id, signal));
  const options = (result.data || []).map((r) => ({ value: r.id, label: r.name }));
  if (selected && !options.some((option) => option.value === selected.id)) options.push({ value: selected.id, label: selected.name || selected.id });
  return <div className="ci-region-level">
    <span className="ci-label">{t(`ci.level${level}`)}</span>
    <Select showSearch optionFilterProp="label" allowClear className="ci-wide"
      aria-label={t(`ci.level${level}`)} placeholder={t('cc.selectRegion')} value={selected?.id}
      disabled={!parent || !cc.snapshot || cc.navigation?.loading || !!cc.navigation?.error} loading={result.status === 'loading'}
      options={options}
      onChange={(id) => {
        const region = result.data?.find((r) => r.id === id);
        const nextPath = region ? [...path, region] : path;
        const next = nextPath.at(-1);
        cc.selectScope({ kind: 'admin', subject_id: next.id, name: next.name, level: next.level, bounds: next.bounds }, nextPath);
      }} />
    <CCResourceState result={result} retry={cc.retry} />
  </div>;
}

export default function CCAdminSelector() {
  const cc = useCC(); const { t } = useTranslation('translation');
  const path = cc.sidebarPath || cc.path || [];
  const enabled = cc.active && cc.snapshot && cc.mode === 'explore' && !path.length && !cc.navigation?.loading && !cc.navigation?.error;
  const result = useCCResource(enabled ? JSON.stringify([cc.accessKey, cc.snapshot.boundary_revision_id, cc.refresh]) : null,
    (signal) => cc.geography ? cc.geography.load(null, signal) : readRegionSiblings(cc.snapshot, null, signal));
  const select = useRef(cc.selectScope); select.current = cc.selectScope;
  useEffect(() => {
    if (enabled && !cc.scope && !cc.navigation?.message && result.data?.length === 1 && result.data[0].level === 'COUNTRY') {
      const region = result.data[0];
      select.current({ kind: 'admin', subject_id: region.id, name: region.name, level: region.level, bounds: region.bounds }, [region]);
    }
  }, [enabled, cc.scope, cc.navigation?.message, result.data]);
  return <div className="ci-admin-selector">
    {path[0]?.level === 'COUNTRY' ? ['PROVINCE', 'DISTRICT', 'VILLAGE'].map((level, index) =>
      <RegionLevel key={level} level={level} parent={path[index]} selected={path[index + 1]} path={path.slice(0, index + 1)} />)
      : path.length > 0 && <RegionLevel level={path.at(-1).level === 'DISTRICT' ? 'VILLAGE' : 'DISTRICT'} parent={path.at(-1)} path={path} />}
    {(result.data?.length > 1 || (result.data?.length > 0 && cc.navigation?.message)) && <Select aria-label={t('ci.country')} placeholder={t('ci.country')} className="ci-wide"
      options={result.data.map((r) => ({ value: r.id, label: r.name }))} onChange={(id) => {
        const r = result.data.find((node) => node.id === id);
        cc.selectScope({ kind: 'admin', subject_id: r.id, name: r.name, level: r.level, bounds: r.bounds }, [r]);
      }} />}
    <CCResourceState result={result} retry={cc.retry} />
    {cc.navigation?.loading && <p role="status" className="ci-muted">{t('ci.loadingGeography')}</p>}
    {cc.navigation?.message && <p role="status" className="ci-muted">{t(cc.navigation.message)}</p>}
    {cc.navigation?.error && <CCError error={cc.navigation.error} retry={cc.navigation.retry || cc.retry} />}
  </div>;
}
